import { D3Graph, D3Node } from '@swimlane/ngx-graph';
import { ArticleNodeAuthor, Publisher, RelatedTo } from '../api/backend-connector/models';

export const getPointFromEvent = (event: TouchEvent | MouseEvent, svg: SVGGraphicsElement) => {
  const point = new DOMPoint();

  // This is a polyfill for `event instanceof TouchEvent` in Safari and Firefox
  if ('targetTouches' in event) {
    point.x = event.targetTouches[0].clientX;
    point.y = event.targetTouches[0].clientY;
  } else {
    point.x = event.clientX;
    point.y = event.clientY;
  }

  const invertedSVGMatrix = svg.getScreenCTM()?.inverse();
  return point.matrixTransform(invertedSVGMatrix);
};

export const getPointFromSvgNode = (node: CustomNode & D3Node, svg: SVGGraphicsElement) => {
  const point = new DOMPoint();
  point.x = node.x;
  point.y = node.y;

  const invertedSVGMatrix = svg.getScreenCTM() ?? undefined;
  return point.matrixTransform(invertedSVGMatrix);
};

export const nodeIntoSelection = (nodes: (CustomNode & D3Node)[], rect: DOMRect, svg: SVGGraphicsElement) => {
  const pointA = new DOMPoint();
  const pointB = new DOMPoint();
  pointA.x = rect.x;
  pointA.y = rect.y;
  pointB.x = rect.x + rect.width;
  pointB.y = rect.y + rect.height;

  const invertedSVGMatrix = svg.getScreenCTM()?.inverse();
  const pointTA = pointA.matrixTransform(invertedSVGMatrix);
  const pointTB = pointB.matrixTransform(invertedSVGMatrix);

  return nodes.filter((n) => pointTA.x < n.x && n.x < pointTB.x && pointTA.y < n.y && n.y < pointTB.y);
};

export const getPointFromLayedOutGraph = (nodes: Set<string>, graph: { nodes: (CustomNode & D3Node)[] } & D3Graph) => {
  const layedOutNodes = graph.nodes.filter((n): n is CustomNode & D3Node => nodes.has(n.id));

  const minx = Math.min(...layedOutNodes.map((n) => n.x));
  const miny = Math.min(...layedOutNodes.map((n) => n.y));
  const maxx = Math.max(...layedOutNodes.map((n) => n.x));
  const maxy = Math.max(...layedOutNodes.map((n) => n.y));

  const point = new DOMPoint();
  point.x = minx + (maxx - minx) * 0.5;
  point.y = miny + (maxy - miny) * 0.5;

  return { point, width: maxx - minx, height: maxy - miny };
};

export const calcViewSize = (rect: DOMRectReadOnly, bbox: { x: number; y: number; width: number; height: number }) => {
  let minx = bbox.x;
  let miny = bbox.y;
  let maxx = bbox.x + bbox.width;
  let maxy = bbox.y + bbox.height;

  // assumes bbox.width == bbox.height
  if (rect.height < rect.width) {
    // wider than tall
    const center = 0.5 * (minx + maxx);
    const width = (bbox.width * rect.width) / rect.height;
    minx = center - width * 0.5;
    maxx = center + width * 0.5;
  } else {
    // taller than wide
    const center = 0.5 * (miny + maxy);
    const height = (bbox.height * rect.height) / rect.width;
    miny = center - height * 0.5;
    maxy = center + height * 0.5;
  }
  return { minx, maxx, miny, maxy };
};

export const fullTextSearch = (A: string, B?: string) => {
  const regex = new RegExp(`\\b${A}\\b`, 'i');
  return B ? regex.test(B) : false;
};

export const calcInitialGraph = (nodes: (CustomNode & D3Node)[]) => {
  const minx = Math.min(...nodes.map((n) => n.x));
  const miny = Math.min(...nodes.map((n) => n.y));
  const maxx = Math.max(...nodes.map((n) => n.x));
  const maxy = Math.max(...nodes.map((n) => n.y));

  const delta = Math.max(maxx - minx, maxy - miny);
  return { minx, miny, delta };
};

export interface CategoryNode {
  id: string;
  label: string | null | undefined;
  title?: string | null;
  color?: string | null;
  nodesCount: number;
  type: 'category';
}

export type CustomNode =
  | {
      date?: string | null;
      id: string;
      category?: string;
      authors: Array<ArticleNodeAuthor>;
      publisher?: Publisher;
      relatedTo: Array<RelatedTo>;
      title?: string | null;
      label: string | null | undefined;
      color?: string | null;
      type: 'article';
    }
  | {
      articlesCount: number;
      id: string;
      category?: string;
      relatedTo: Array<RelatedTo>;
      title?: string | null;
      label: string | null | undefined;
      color?: string | null;
      type: 'topic';
    }
  | CategoryNode;

export const colors = [
  '#22D68D',
  '#1FD096',
  '#1DCA9F',
  '#1AC4A9',
  '#18BEB2',
  '#15B7BB',
  '#13B1C4',
  '#10ABCE',
  '#0EA5D7',
  '#0B9FE0',
];

export const filterColors = [
  '#38bdf8',
  '#dc2626',
  '#059669',
  '#7c3aed',
  '#ca8a04',
  '#2dd4bf',
  '#818cf8',
  '#4ade80',
  '#fb923c',
  '#ea580c',
  '#0891b2',
  '#d946ef',
  '#2563eb',
  '#c084fc',
  '#65a30d',
  '#f87171',
  '#c026d3',
  '#db2777',
  '#276456',
  '#eab308',
];

export const CATEGORY = '#475569';
export const TOPIC = '#64748b';
export const ARTICLE = '#94a3b8';
export const LINKLESS_ARTICLE = '#cbd5e1';
export const UNFOCUSED_ARTICLE = '#e2e8f0';
export const EDGE = '#92cccf';
export const TOPIC_WIDTH = 80;
export const CATEGORY_WIDTH = 70;
export const ARTICLE_WIDTH = 30;
export const SELECTION_FACTOR = 4.5; // on node selection
export const FACTOR = 0.8;
